import React from 'react';
import { bool, node, func } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { captureException } from '../../util/log';
import { Button } from '../../components';
import css from './ErrorBoundary.module.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    captureException(error, 'react-error-boundary', {
      errorBoundary: true,
      errorId,
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorId);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className={css.errorBoundary}>
          <div className={css.errorContainer}>
            <div className={css.errorIcon}>⚠️</div>
            <h2 className={css.errorTitle}>
              <FormattedMessage id="ErrorBoundary.title" defaultMessage="Something went wrong" />
            </h2>
            <p className={css.errorMessage}>
              <FormattedMessage
                id="ErrorBoundary.message"
                defaultMessage="We're sorry, but something unexpected happened. Please try again."
              />
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={css.errorDetails}>
                <summary>Error Details (Development Only)</summary>
                <pre className={css.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className={css.errorActions}>
              <Button onClick={this.handleRetry} className={css.retryButton}>
                <FormattedMessage id="ErrorBoundary.retry" defaultMessage="Try Again" />
              </Button>
              <Button onClick={this.handleReload} className={css.reloadButton} variant="secondary">
                <FormattedMessage id="ErrorBoundary.reload" defaultMessage="Reload Page" />
              </Button>
            </div>

            {this.state.errorId && (
              <p className={css.errorId}>
                <FormattedMessage
                  id="ErrorBoundary.errorId"
                  defaultMessage="Error ID: {errorId}"
                  values={{ errorId: this.state.errorId }}
                />
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: node.isRequired,
  fallback: func,
  onError: func,
  onRetry: func,
  showDetails: bool,
};

ErrorBoundary.defaultProps = {
  showDetails: process.env.NODE_ENV === 'development',
};

export default ErrorBoundary;
